/**
 * Upload content policy — single source of truth for which files the platform
 * accepts for applicant documents, labor category PDTs and applicant signatures.
 *
 * ACP-2145: the CCIC pentest uploaded an SVG through a presigned S3 URL because
 * each layer kept its own copy of "what is allowed" and none of them agreed:
 * the route schema capped uploads at 10 MB, the service at 4 MB, the UI derived
 * MIME types from a hand-written chain, and nothing cross-checked the file
 * extension against the declared MIME type. Everything in this module is pure
 * data plus string helpers so the browser, the Fastify route schemas and the
 * services can all import it.
 *
 * Magic-byte sniffing (Node-only) deliberately lives elsewhere:
 * `entity-manager/src/utils/upload-content-policy.ts`.
 *
 *   filename ──► extractFileExtension ──► UPLOAD_EXTENSIONS_BY_MIME ──► AllowedUploadMime
 *                     (strict)                    (reverse lookup)
 */

/** MIME types accepted for applicant document uploads. */
export const ALLOWED_UPLOAD_MIME = ['application/pdf', 'image/jpeg', 'image/png'] as const;

export type AllowedUploadMime = (typeof ALLOWED_UPLOAD_MIME)[number];

/**
 * Extensions that legitimately carry each MIME type.
 *
 * Order matters only for {@link DOCUMENT_UPLOAD_ACCEPTED_FORMATS}, which feeds
 * the "Supported formats: PDF, JPG, JPEG, PNG" hint in the upload widget.
 */
export const UPLOAD_EXTENSIONS_BY_MIME: Record<AllowedUploadMime, readonly string[]> = {
  'application/pdf': ['pdf'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
};

/** The only MIME type accepted for a labor category PDT. */
export const PDT_UPLOAD_MIME: AllowedUploadMime = 'application/pdf';

/** The only MIME type accepted for an applicant signature image. */
export const SIGNATURE_UPLOAD_MIME: AllowedUploadMime = 'image/png';

/**
 * Maximum size of an applicant document upload.
 *
 * Replaces the previous disagreement between the route schema (10 MB), the
 * service (4 MB) and the UI (4 MB). The presigned URL now signs the exact
 * content length, so this is the value clients must respect.
 */
export const MAX_DOCUMENT_UPLOAD_MB = 4;
export const MAX_DOCUMENT_UPLOAD_BYTES = MAX_DOCUMENT_UPLOAD_MB * 1024 * 1024;

/**
 * Lifetime of a presigned upload URL.
 *
 * Shortened from 15 minutes: the client PUTs immediately after reserving, and a
 * shorter window narrows the period in which a leaked URL could be replayed.
 * Download URLs keep their own (longer) expiry.
 */
export const UPLOAD_URL_TTL_SECONDS = 300;

/** Uppercase extension labels shown in the file upload widget. */
export const DOCUMENT_UPLOAD_ACCEPTED_FORMATS: readonly string[] = ALLOWED_UPLOAD_MIME.flatMap((mime) =>
  UPLOAD_EXTENSIONS_BY_MIME[mime].map((extension) => extension.toUpperCase()),
);

/** User-facing message for a file whose real content is not what it claims. */
export const UPLOAD_CONTENT_REJECTED_MESSAGE =
  'The uploaded file does not match its declared type. Please upload a PDF, JPG, JPEG, or PNG file.';

/** User-facing message for a declared type/extension the platform does not accept. */
export const UNSUPPORTED_UPLOAD_TYPE_MESSAGE =
  'The file type is not supported. Please upload a PDF, JPG, JPEG, or PNG file.';

/**
 * Extract a filename's extension, strictly.
 *
 * Returns `null` — meaning "no usable extension" — for names without a dot
 * (`resume`), dotfiles (`.env`), trailing dots (`file.`) and any extension
 * carrying characters outside `[a-zA-Z0-9]`. Strictness is deliberate: a lenient
 * reader that strips odd bytes would turn `payload.pd!f` into `pdf` and hand an
 * attacker the extension check for free.
 */
export function extractFileExtension(filename: string): string | null {
  const separatorIndex = filename.lastIndexOf('.');

  if (separatorIndex <= 0 || separatorIndex === filename.length - 1) {
    return null;
  }

  const rawExtension = filename.slice(separatorIndex + 1);

  if (!/^[a-zA-Z0-9]+$/.test(rawExtension)) {
    return null;
  }

  return rawExtension.toLowerCase();
}

/** Narrow an arbitrary string to an allowlisted upload MIME type. */
export function isAllowedUploadMime(value: string | null | undefined): value is AllowedUploadMime {
  return typeof value === 'string' && (ALLOWED_UPLOAD_MIME as readonly string[]).includes(value);
}

/**
 * The MIME type a filename's extension implies, or `null` when the extension is
 * missing or not one this platform accepts.
 */
export function mimeForFilename(filename: string): AllowedUploadMime | null {
  const extension = extractFileExtension(filename);

  if (!extension) {
    return null;
  }

  return ALLOWED_UPLOAD_MIME.find((mime) => UPLOAD_EXTENSIONS_BY_MIME[mime].includes(extension)) ?? null;
}

/**
 * True when the filename's extension belongs to the declared MIME type.
 *
 * Blocks the pentest's rename path (`payload.svg` declared as `image/png`) and
 * the reverse (`resume.pdf` declared as `image/jpeg`) before a presigned URL is
 * ever issued.
 */
export function extensionMatchesMime(filename: string, declaredMime: string): boolean {
  const impliedMime = mimeForFilename(filename);

  return impliedMime !== null && impliedMime === declaredMime;
}
